import get from "lodash/get";
import { useMemo, useState } from "react";
import styled from "styled-components";

import { faChevronDown, faChevronUp, faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Accordion from "components/molecules/Accordion";
import LoadingAnimation from "components/organisms/LoadingAnimation";

/**
 * Table creates a table with sortable columns based on the objects and labels provided.
 * This component requires a type parameter T, which is the type of the table entry objects.
 * To include a property of the object in the table, add a label for it using `labels={ key: "Label" }`.
 * Nested properties can also be added by using string syntax: `labels={ "key.subkey": "Label" }`.
 * If values need to be generated before displaying, add a generator function in the same way as labels: `generators={ key: (v: T) => v }`.
 * If values need to be sorted in a specific way, add a sort function in the same way as labels: `sorters={ key: (a: any, b: any) => number }`.
 * To add an action to each row, use the action prop to pass a function: `action={(entry: T) => { <Button>Edit {entry.name}</Button> }}`
 */
export function AccordionTable<T>({
	entries = [],
	labels,
	content,
	generators,
	sorters,
	loading,
	error,
	columnPercentages = [],
	rowKey,
}: {
	readonly entries?: T[];
	readonly labels: Partial<{ [key in keyof T]: string }> | { [key in string]: string };
	readonly content: (v: T) => React.ReactNode;
	readonly generators?: Partial<{ [key in keyof T]: (v: T) => React.ReactNode }> | { [key in string]: (v: T) => React.ReactNode };
	readonly sorters?: Partial<{ [key in keyof T]: (a: unknown, b: unknown) => number }> | { [key in string]: (a: unknown, b: unknown) => number };
	readonly loading?: boolean;
	readonly error?: boolean;
	readonly columnPercentages?: number[];
	readonly rowKey?: (entry: T) => React.Key;
}) {
	const [sortedBy, setSortedBy] = useState<string>();
	const [reversed, setReversed] = useState(false);

	const sortedEntries = useMemo(() => {
		if (!sortedBy) return [...entries];

		/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */
		const customSorterForKey = sorters ? (sorters[sortedBy] as (a: unknown, b: unknown) => number) : false;

		if (customSorterForKey)
			return [...entries].sort((a, b) => {
				const result = customSorterForKey(get(a, sortedBy), get(b, sortedBy));
				return reversed ? -result : result;
			});

		return [...entries].sort((a, b) => {
			const result = get(a, sortedBy) < get(b, sortedBy) ? -1 : 1;
			return reversed ? -result : result;
		});
	}, [entries, sortedBy, reversed, sorters]);

	// This function toggles through Inactive -> Active -> Active (reverse) -> Inactive
	const headerOnClick = (key: string) => {
		if (sortedBy === key) {
			if (reversed) {
				setSortedBy(undefined);
				setReversed(false);
			} else {
				setReversed(true);
			}
		} else {
			setSortedBy(key);
			setReversed(false);
		}
	};

	const labelKeys = Object.keys(labels);

	return (
		<>
			<TableWrapper>
				<TableRoot $columnWidths={columnPercentages}>
					<thead>
						<tr>
							{labelKeys.map((key) => (
								<th key={key}>
									<TableHeaderButton onClick={() => headerOnClick(key)}>
										{/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */}

										{labels[key]}
										<TableHeaderIcon icon={sortedBy === key && reversed ? faChevronUp : faChevronDown} $active={sortedBy === key} />
									</TableHeaderButton>
								</th>
							))}
						</tr>
					</thead>
				</TableRoot>
			</TableWrapper>
			<div>
				{loading ? (
					<LoadingContainer>
						<LoadingAnimation />
					</LoadingContainer>
				) : (
					sortedEntries.map((entry, i) => (
						<Accordion
							key={rowKey?.(entry) ?? i}
							style={{ margin: "0.2em 0", padding: "0 0" }}
							header={
								<AccordionTableRow>
									{labelKeys.map((key, i) => (
										<AccordionTableCell key={key} $width={columnPercentages[i] + "%"}>
											{/* @ts-ignore To allow "a.b.c.d" syntax on nested objects (which does not fit with keyof T) */}
											{generators?.[key] ? generators[key]!!(entry) : get(entry, key)}
										</AccordionTableCell>
									))}
								</AccordionTableRow>
							}
						>
							{content(entry)}
						</Accordion>
					))
				)}
				{((!sortedEntries.length && !loading) || error) && (
					<>
						{error ? (
							<>
								<FontAwesomeIcon size="1x" color="darkred" icon={faXmarkCircle} /> Henting av data feilet
							</>
						) : (
							"Ingen data å vise"
						)}
					</>
				)}
			</div>
		</>
	);
}

const TableWrapper = styled.div`
	padding: 0 0.5em;
	width: 100%;
`;

const TableRoot = styled.table<{ $columnWidths?: number[] }>`
	position: relative;
	border-collapse: collapse;
	width: 100%;
	tr:last-child {
		border-top: none;
	}

	th {
		font-weight: bold;
	}

	${({ $columnWidths }) =>
		$columnWidths
			?.map(
				(width, index) => `
        th:nth-child(${index + 1}) {
          width: ${width}%;
        }
      `
			)
			.join("")}
`;

const TableHeaderButton = styled.button`
	font-size: 0.875rem;
	font-weight: 700;
	display: flex;
	gap: 0.5rem;
	align-items: center;
	border: none;
	background: none;
	color: inherit;
	padding: 0;
	margin: 0;
	&:hover {
		background: transparent;
		color: ${(props) => props.theme.colors.text};
		border: 0;
	}
`;

const TableHeaderIcon = styled(FontAwesomeIcon)<{ $active: boolean }>`
	font-size: 0.8rem;
	color: ${(props) => (!props.$active ? "var(--nte-graa-mork)" : "var(--nte-blaa-logo)")};
`;

const LoadingContainer = styled.div`
	margin: 0;
	padding: 0 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
`;

const AccordionTableRow = styled.div`
	display: table;
	width: 100%;
	table-layout: fixed;
`;

const AccordionTableCell = styled.div<{ $width: string }>`
	display: table-cell;
	width: ${(props) => props.$width};
	padding: 0.5rem 0rem; // Match the th padding
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
